/* eslint-disable no-console */

"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";

interface AuthStatusContextType {
  isAuthenticated: boolean | null;
  loading: boolean;
  checkAuth: () => Promise<void>;
  checkAuthAndUpdateSession: () => Promise<void>;
}

const AuthStatusContext = createContext<AuthStatusContextType | undefined>(undefined);

export const AuthStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { update, status } = useSession();

  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/auth/token", { credentials: "include" });
      const data = await res.json();
      setIsAuthenticated(!!data.success);
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Call this after any token refresh or silent login
  const checkAuthAndUpdateSession = useCallback(async () => {
    await checkAuth();
    if (typeof update === "function") {
      await update();
    }
  }, [checkAuth, update]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // If we don't know auth state, and session is not authenticated, try to hydrate
    if (isAuthenticated === null && status !== "authenticated") {
      checkAuthAndUpdateSession();
    }
  }, [isAuthenticated, status, checkAuthAndUpdateSession]);

  const contextValue = React.useMemo(
    () => ({ isAuthenticated, loading, checkAuth, checkAuthAndUpdateSession }),
    [isAuthenticated, loading, checkAuth, checkAuthAndUpdateSession]
  );

  return (
    <AuthStatusContext.Provider value={contextValue}>
      {children}
    </AuthStatusContext.Provider>
  );
};

export function useAuthStatusContext() {
  const ctx = useContext(AuthStatusContext);
  if (!ctx) throw new Error("useAuthStatusContext must be used within AuthStatusProvider");
  return ctx;
}
